/**
 * "Manage data" - everything that rewrites the register, behind one button.
 *
 * Before this there were seven controls parked in the page head: three sync
 * buttons, a ranking start button, and the three readouts wrapped around them.
 * They are all rare - a mirror is refreshed on a schedule, and an operator opens
 * this page to work a list, not to run an import - so they cost the top of every
 * visit and paid nothing back.
 *
 * The menu keeps them together with the numbers that decide whether to press
 * them: how many companies are on file and when the mirror last landed, then how
 * much of it is ranked and when. A button to recompute a ranking is meaningless
 * without the date beside it.
 */
import { FileSpreadsheet, Gauge, Landmark, Loader2, Play, RefreshCw, Receipt, Square, Wrench } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { Btn, fmtDateTime, fmtNum } from "../../../ui";
import { Pop } from "../../../shell/Pop";
import type { SyncState } from "./useSyncStatus";
import type { WebNeedPassState } from "./useWebNeedPass";

export function DataMenu({ sync, pass }: { sync: SyncState; pass: WebNeedPassState }) {
  const t = useT();
  const { running, lastRun, busy, message, start } = sync;
  const progress = pass.progress;
  const scored = progress ? progress.total - progress.bands.none : 0;

  return (
    <Pop
      wide
      trigger={(open) => (
        <Btn tone="dark" aria-haspopup="menu" aria-expanded={open}>
          <Wrench className="size-4" />
          {t("ariregister.data.open")}
          {running || progress?.running ? <Loader2 className="size-3.5 animate-spin" /> : null}
        </Btn>
      )}
    >
      <div className="crm-menu-head">
        <div className="crm-menu-t">{t("ariregister.data.mirror")}</div>
        <div className="crm-menu-s crm-tnum">
          {t("ariregister.sync.count", { count: fmtNum(sync.status?.stats.activeCount ?? 0) })}
          {" · "}
          {lastRun
            ? t("ariregister.sync.last", { date: fmtDateTime(lastRun.finishedAt || lastRun.startedAt) })
            : t("ariregister.sync.never")}
        </div>
      </div>

      <button type="button" className="crm-mi" onClick={() => void start("all")} disabled={busy || !!running}>
        <RefreshCw className={cn("size-4", busy && "animate-spin")} />
        <span className="flex-1">
          {t("ariregister.sync.now")}
          <span className="crm-mi-sub block">{t("ariregister.sync.allHint")}</span>
        </span>
      </button>
      <button type="button" className="crm-mi" onClick={() => void start("tax")} disabled={busy || !!running}>
        <Receipt className="size-4" />
        <span className="flex-1">
          {t("ariregister.sync.tax")}
          <span className="crm-mi-sub block">{t("ariregister.sync.taxHint")}</span>
        </span>
      </button>
      <button type="button" className="crm-mi" onClick={() => void start("reports")} disabled={busy || !!running}>
        <FileSpreadsheet className="size-4" />
        <span className="flex-1">
          {t("ariregister.sync.reports")}
          <span className="crm-mi-sub block">{t("ariregister.sync.reportsHint")}</span>
        </span>
      </button>
      <button type="button" className="crm-mi" onClick={() => void start("procurements")} disabled={busy || !!running}>
        <Landmark className="size-4" />
        <span className="flex-1">
          {t("ariregister.sync.procurements")}
          <span className="crm-mi-sub block">{t("ariregister.sync.procurementsHint")}</span>
        </span>
      </button>

      {/* A detached background job - a firm word that the page may be closed. */}
      {running ? (
        <p className="crm-menu-note">{t("ariregister.sync.background")}</p>
      ) : message ? (
        <p className="crm-menu-note">{message}</p>
      ) : null}

      <div className="crm-menu-sep" />

      <div className="crm-menu-head">
        <div className="crm-menu-t">{t("ariregister.webNeed.title")}</div>
        <div className="crm-menu-s crm-tnum">
          {progress
            ? `${t("ariregister.data.scored", { done: fmtNum(scored), total: fmtNum(progress.total) })} · ${
                progress.scored === 0
                  ? t("ariregister.webNeed.panel.never")
                  : t("ariregister.webNeed.panel.computedAt", { at: fmtDateTime(progress.computedAt) })
              }`
            : t("ariregister.loading")}
        </div>
      </div>

      {progress?.running ? (
        <button type="button" className="crm-mi" onClick={() => void pass.stop()} disabled={pass.busy}>
          <Square className="size-4" />
          <span className="flex-1">
            {t("ariregister.webNeed.panel.stop")}
            <span className="crm-mi-sub block crm-tnum">
              {t("ariregister.webNeed.panel.running", {
                done: fmtNum(progress.doneThisRun),
                total: fmtNum(progress.total),
              })}
            </span>
          </span>
        </button>
      ) : (
        <button type="button" className="crm-mi" onClick={() => void pass.start()} disabled={pass.busy || !progress}>
          {pass.busy ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
          <span className="flex-1">
            {t("ariregister.webNeed.panel.start")}
            <span className="crm-mi-sub block">{t("ariregister.data.scoreHint")}</span>
          </span>
        </button>
      )}

      {progress?.lastError ? (
        <p className="crm-menu-note crm-menu-note-bad">
          <Gauge className="size-3.5" />
          {progress.lastError}
        </p>
      ) : null}
    </Pop>
  );
}
